/**
 * Sawyer 08 meds-feeding — the words staff read for care times and dose outcomes.
 * One table so the Meds & feeding report, the med pass, the profile editor
 * and the care log never say two different things about the same slot
 * (refuter C-11: the care log used to say "Morning" where the report says
 * "AM").
 */

export const SLOT_ORDER = ['am', 'midday', 'pm', 'bedtime', 'as_needed'] as const;
export type CareSlot = (typeof SLOT_ORDER)[number];

export const SLOT_LABEL: Readonly<Record<CareSlot, string>> = {
  am: 'AM',
  midday: 'Noon',
  pm: 'PM',
  bedtime: 'Bedtime',
  as_needed: 'As needed (PRN)',
};

/** The short chip word (the editor's toggles, the Day list line). */
export const SLOT_SHORT: Readonly<Record<CareSlot, string>> = {
  am: 'AM',
  midday: 'Noon',
  pm: 'PM',
  bedtime: 'Bedtime',
  as_needed: 'As needed',
};

export const MEAL_ORDER = ['breakfast', 'lunch', 'dinner'] as const;
export type CareMeal = (typeof MEAL_ORDER)[number];

export const MEAL_LABEL: Readonly<Record<CareMeal, string>> = {
  breakfast: 'Breakfast',
  lunch: 'Lunch',
  dinner: 'Dinner',
};

/**
 * Dose outcomes as staff read them. `given` is "Administered" (Sawyer's
 * word on the med pass); the stored value is unchanged so existing rows and
 * the customer serializer keep working.
 */
export const DOSE_OUTCOME_LABEL: Readonly<Record<string, string>> = {
  prepared: 'Prepared',
  given: 'Administered',
  partially_administered: 'Partly given',
  refused: 'Refused',
  missed: 'Missed',
};

export function slotLabel(slot: string): string {
  return (SLOT_LABEL as Record<string, string>)[slot] ?? 'Other time';
}

export function slotShort(slot: string): string {
  return (SLOT_SHORT as Record<string, string>)[slot] ?? 'Other time';
}

export function mealLabel(meal: string): string {
  return (MEAL_LABEL as Record<string, string>)[meal] ?? 'Meal';
}

export function doseOutcomeLabel(outcome: string): string {
  return DOSE_OUTCOME_LABEL[outcome] ?? 'Recorded';
}

/** "AM, PM" from slot keys, in day order, unknown words skipped. */
export function formatSlots(slots: readonly string[] | null | undefined): string {
  if (!slots || slots.length === 0) return '';
  const set = new Set(slots);
  return SLOT_ORDER.filter((s) => set.has(s))
    .map((s) => SLOT_SHORT[s])
    .join(', ');
}

/** "Breakfast, Dinner" from meal keys, in day order. */
export function formatMeals(meals: readonly string[] | null | undefined): string {
  if (!meals || meals.length === 0) return '';
  const set = new Set(meals);
  return MEAL_ORDER.filter((m) => set.has(m))
    .map((m) => MEAL_LABEL[m])
    .join(', ');
}

/** "8:04 AM" in the facility's zone (falls back to the browser's). */
export function formatClock(iso: string, timeZone?: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  try {
    return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone }).format(d);
  } catch {
    return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(d);
  }
}
